import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { 
  HiOutlineBriefcase, 
  HiOutlineSun, 
  HiOutlineMoon,
  HiOutlineArrowRightOnRectangle,
  HiOutlineChevronDown
} from 'react-icons/hi2';
import styles from './Header.module.css';

const Header = () => {
  const { isDark, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

  const handleLogout = async () => {
    try {
      await logout();
      setShowUserMenu(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getDisplayName = () => {
    if (user?.displayName) return user.displayName;
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  const getInitials = () => {
    const name = getDisplayName();
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        <div className={styles.brand}>
          <div className={styles.logo}>
            <HiOutlineBriefcase className={styles.logoIcon} />
            <div className={styles.brandText}>
              <h1 className={styles.title}>JobTracker</h1>
              <span className={styles.subtitle}>Professional Application Management</span>
            </div>
          </div>
        </div>
        
        <div className={styles.actions}>
          <button
            onClick={toggleTheme}
            className={styles.themeToggle}
            aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
            title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
          >
            {isDark ? (
              <HiOutlineSun className={styles.themeIcon} />
            ) : (
              <HiOutlineMoon className={styles.themeIcon} />
            )}
          </button>

          {user && !isDemoMode && (
            <div className={styles.userMenu} ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className={styles.userButton}
                aria-label="User menu"
              >
                <div className={styles.userAvatar}>
                  {user.photoURL ? (
                    <img src={user.photoURL} alt="Profile" className={styles.avatarImage} />
                  ) : (
                    <span className={styles.avatarInitials}>{getInitials()}</span>
                  )}
                </div>
                <span className={styles.userName}>{getDisplayName()}</span>
                <HiOutlineChevronDown className={styles.chevronIcon} />
              </button>

              {showUserMenu && (
                <div className={styles.userDropdown}>
                  <div className={styles.userInfo}>
                    <div className={styles.userAvatar}>
                      {user.photoURL ? (
                        <img src={user.photoURL} alt="Profile" className={styles.avatarImage} />
                      ) : (
                        <span className={styles.avatarInitials}>{getInitials()}</span>
                      )}
                    </div>
                    <div className={styles.userDetails}>
                      <div className={styles.userDisplayName}>{getDisplayName()}</div>
                      <div className={styles.userEmail}>{user.email}</div>
                    </div>
                  </div>
                  <div className={styles.menuDivider}></div>
                  <button onClick={handleLogout} className={styles.logoutButton}>
                    <HiOutlineArrowRightOnRectangle className={styles.logoutIcon} />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;